/**
 * ArticleView — renderização COMPARTILHADA do artigo (página + widget).
 *
 * Estilo Central de Ajuda da Cakto: breadcrumb, cabeçalho (título + resumo +
 * autor/data), vídeo (se houver), índice automático a partir dos H2/H3 do
 * markdown (com scroll-spy) e corpo em markdown seguro (react-markdown, SEM HTML
 * cru -> XSS-safe; URLs sanitizadas por padrão).
 *
 * layout="page"   -> índice lateral sticky à direita; rola a janela.
 * layout="widget" -> índice recolhível no topo; rola dentro do container (scrollRoot).
 *
 * Imagens são ILUSTRATIVAS (a IA lê só o texto do artigo) — aqui só exibimos.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown, ChevronRight, ExternalLink, List } from 'lucide-react';

import { slugify, childrenToText, extractHeadings, normalizeMarkdown } from './articleMarkdown';
import CodeBlock from './CodeBlock';

const extractYoutubeId = (url) => {
  if (!url) return null;
  const m = url.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
  return (m && m[2].length === 11) ? m[2] : null;
};

const authorName = (createdBy) => {
  if (!createdBy) return null;
  const local = String(createdBy).split('@')[0];
  return local.charAt(0).toUpperCase() + local.slice(1);
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return null;
  }
};

// Componentes de render do markdown — estáticos (slug por texto: índice e
// heading batem). NÃO renderiza HTML cru (react-markdown default) => XSS-safe.
const heading = (Tag, baseStyle) => ({ children }) => {
  const id = slugify(childrenToText(children));
  return <Tag id={id} data-help-heading={Tag} style={baseStyle}>{children}</Tag>;
};

const MD_COMPONENTS = {
  h1: heading('h2', { fontSize: '20px', fontWeight: 700, color: 'var(--dp-text-primary)', margin: '28px 0 12px' }),
  h2: heading('h2', { fontSize: '20px', fontWeight: 700, color: 'var(--dp-text-primary)', margin: '28px 0 12px' }),
  h3: heading('h3', { fontSize: '16px', fontWeight: 600, color: 'var(--dp-text-primary)', margin: '20px 0 8px' }),
  p: ({ children }) => <p style={{ fontSize: '15px', lineHeight: 1.75, color: 'var(--dp-text-secondary)', margin: '0 0 14px' }}>{children}</p>,
  ul: ({ children }) => <ul style={{ margin: '0 0 14px', paddingLeft: '22px', color: 'var(--dp-text-secondary)', fontSize: '15px', lineHeight: 1.7 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '0 0 14px', paddingLeft: '22px', color: 'var(--dp-text-secondary)', fontSize: '15px', lineHeight: 1.7 }}>{children}</ol>,
  li: ({ children }) => <li style={{ margin: '4px 0' }}>{children}</li>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--dp-blue-500)', textDecoration: 'underline' }}>{children}</a>
  ),
  img: ({ src, alt }) => (
    <img src={src} alt={alt || ''} loading="lazy" style={{ maxWidth: '100%', height: 'auto', borderRadius: '10px', margin: '10px 0', display: 'block' }} />
  ),
  strong: ({ children }) => <strong style={{ color: 'var(--dp-text-primary)', fontWeight: 700 }}>{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  blockquote: ({ children }) => (
    <blockquote style={{ borderLeft: '3px solid var(--dp-blue-500)', margin: '0 0 14px', padding: '4px 0 4px 14px', color: 'var(--dp-text-secondary)' }}>{children}</blockquote>
  ),
  code: ({ children }) => <code style={{ background: 'var(--dp-bg-input)', padding: '2px 6px', borderRadius: '6px', fontSize: '13px' }}>{children}</code>,
  // Bloco de código (```) com botão COPIAR — comandos colados sem erro.
  pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
  hr: () => <hr style={{ border: 'none', borderTop: '1px solid var(--dp-border-subtle)', margin: '20px 0' }} />,
  table: ({ children }) => <div style={{ overflowX: 'auto' }}><table style={{ borderCollapse: 'collapse', width: '100%', margin: '0 0 14px', fontSize: '14px' }}>{children}</table></div>,
  th: ({ children }) => <th style={{ border: '1px solid var(--dp-border-subtle)', padding: '6px 10px', textAlign: 'left', color: 'var(--dp-text-primary)' }}>{children}</th>,
  td: ({ children }) => <td style={{ border: '1px solid var(--dp-border-subtle)', padding: '6px 10px', color: 'var(--dp-text-secondary)' }}>{children}</td>,
};

export default function ArticleView({
  article,
  layout = 'page',
  scrollRoot = null,            // DOM element (widget) ou null (página/janela)
  onNavigateHome,
  onNavigateCategory,
  onFullscreen,
  sideToc = true,              // página: mostra o índice lateral (desligar em mobile)
}) {
  const rootRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  const [tocOpen, setTocOpen] = useState(false);

  // Normaliza headings colados (##Texto -> ## Texto) pra seções + índice funcionarem.
  const md = useMemo(() => normalizeMarkdown(article?.conteudo_rich || ''), [article?.conteudo_rich]);
  const headings = useMemo(() => extractHeadings(md), [md]);
  const ytId = extractYoutubeId(article?.video_url);
  // Autor: nome do perfil (JOIN por email no backend) com fallback pro email; avatar do perfil.
  const author = article?.author_name || authorName(article?.created_by);
  const authorAvatar = article?.author_avatar;
  // Data: se o artigo foi EDITADO depois de criado (>1 min), mostra a atualização.
  const wasUpdated = article?.updated_at && article?.created_at
    && (new Date(article.updated_at) - new Date(article.created_at) > 60000);
  const date = wasUpdated
    ? `Atualizado em ${formatDate(article.updated_at)}`
    : formatDate(article?.created_at);

  // Scroll-spy: marca a seção ativa conforme o scroll.
  useEffect(() => {
    if (!headings.length || !rootRef.current) return undefined;
    const els = Array.from(rootRef.current.querySelectorAll('[data-help-heading]'));
    if (!els.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries.filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (vis[0]) setActiveId(vis[0].target.id);
      },
      {
        root: scrollRoot || null,
        rootMargin: scrollRoot ? '0px 0px -70% 0px' : '-80px 0px -70% 0px',
        threshold: 0,
      },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [headings, scrollRoot]);

  const scrollToHeading = (id) => {
    const el = rootRef.current?.querySelector(`[id="${id}"]`);
    if (!el) return;
    if (scrollRoot) {
      const top = el.getBoundingClientRect().top - scrollRoot.getBoundingClientRect().top + scrollRoot.scrollTop - 8;
      scrollRoot.scrollTo({ top, behavior: 'smooth' });
    } else {
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setActiveId(id);
  };

  const Toc = ({ compact }) => (
    <nav style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {!compact && (
        <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--dp-text-tertiary)', marginBottom: '8px' }}>
          Nesta página
        </div>
      )}
      {headings.map((h) => (
        <button
          key={h.id}
          onClick={() => { scrollToHeading(h.id); if (compact) setTocOpen(false); }}
          style={{
            textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
            padding: '5px 8px', borderRadius: '6px', fontFamily: 'var(--dp-font)',
            fontSize: '12.5px', lineHeight: 1.4,
            paddingLeft: h.level === 3 ? '20px' : '8px',
            color: activeId === h.id ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
            fontWeight: activeId === h.id ? 600 : 400,
            borderLeft: activeId === h.id ? '2px solid var(--dp-blue-500)' : '2px solid transparent',
          }}
        >
          {h.text}
        </button>
      ))}
    </nav>
  );

  const Header = (
    <header style={{ marginBottom: '8px' }}>
      {/* Breadcrumb (página) */}
      {layout === 'page' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', fontSize: '12.5px', color: 'var(--dp-text-tertiary)', marginBottom: '14px' }}>
          <button onClick={onNavigateHome} style={crumbBtn}>Central de Ajuda</button>
          {article?.categoria && (<>
            <ChevronRight size={13} />
            <button onClick={() => onNavigateCategory?.(article.categoria)} style={crumbBtn}>{article.categoria}</button>
          </>)}
          <ChevronRight size={13} />
          <span style={{ color: 'var(--dp-text-secondary)' }}>{article?.titulo}</span>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
        <h1 style={{ fontSize: layout === 'widget' ? '19px' : '28px', fontWeight: 700, color: 'var(--dp-text-primary)', margin: 0, lineHeight: 1.25 }}>
          {article?.titulo}
        </h1>
        {onFullscreen && (
          <button onClick={onFullscreen} title="Abrir na Central de Ajuda" style={{
            flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dp-text-tertiary)',
            display: 'flex', padding: '4px',
          }}><ExternalLink size={16} /></button>
        )}
      </div>

      {article?.resumo && (
        <p style={{ fontSize: layout === 'widget' ? '14px' : '16px', color: 'var(--dp-text-tertiary)', margin: '8px 0 0', lineHeight: 1.5 }}>
          {article.resumo}
        </p>
      )}

      {(author || date) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px' }}>
          {authorAvatar ? (
            <img src={authorAvatar} alt={author || 'Autor'} style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          ) : (
            <div style={{
              width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, #0DAEFF, #3B82F6)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700,
            }}>{(author || '?').trim().charAt(0).toUpperCase()}</div>
          )}
          <div style={{ lineHeight: 1.3 }}>
            {author && <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-secondary)' }}>{author}</div>}
            {date && <div style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)' }}>{date}</div>}
          </div>
        </div>
      )}

      {ytId && (
        <div style={{ position: 'relative', paddingTop: '56.25%', margin: '18px 0 4px', borderRadius: '12px', overflow: 'hidden', background: '#000' }}>
          <iframe src={`https://www.youtube.com/embed/${ytId}`} title={article?.titulo}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} />
        </div>
      )}
      {!ytId && article?.video_url && (
        <a href={article.video_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', margin: '12px 0 0', color: 'var(--dp-blue-500)', fontSize: '13px' }}>
          Assistir vídeo →
        </a>
      )}
    </header>
  );

  const Body = (
    <div ref={rootRef} style={{ marginTop: '18px' }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS} skipHtml>
        {md}
      </ReactMarkdown>
    </div>
  );

  // ── WIDGET: índice recolhível no topo + corpo (rola dentro do scrollRoot) ──
  if (layout === 'widget') {
    return (
      <div>
        {Header}
        {headings.length > 1 && (
          <div style={{ margin: '14px 0', border: '1px solid var(--dp-border-subtle)', borderRadius: '10px', overflow: 'hidden' }}>
            <button onClick={() => setTocOpen((v) => !v)} style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '9px 12px', background: 'var(--dp-bg-card)', border: 'none', cursor: 'pointer',
              color: 'var(--dp-text-secondary)', fontSize: '12.5px', fontWeight: 600, fontFamily: 'var(--dp-font)',
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><List size={14} /> Nesta página</span>
              <ChevronDown size={15} style={{ transform: tocOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>
            {tocOpen && <div style={{ padding: '6px 8px 10px', borderTop: '1px solid var(--dp-border-subtle)' }}><Toc compact /></div>}
          </div>
        )}
        {Body}
      </div>
    );
  }

  // ── PÁGINA: corpo + índice lateral sticky à direita ──
  return (
    <div style={{ display: 'flex', gap: '40px', alignItems: 'flex-start' }}>
      <article style={{ flex: '1 1 0', minWidth: 0, maxWidth: '760px' }}>
        {Header}
        {Body}
      </article>
      {sideToc && headings.length > 0 && (
        <aside style={{ flex: '0 0 220px', position: 'sticky', top: '84px', alignSelf: 'flex-start' }}>
          <Toc />
        </aside>
      )}
    </div>
  );
}

const crumbBtn = {
  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
  color: 'var(--dp-text-tertiary)', fontSize: '12.5px', fontFamily: 'var(--dp-font)',
};
